<template>
  <section class="form-fw__content form-fw__content--small">
    <form
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <div class="form__container__content pb-1">
          <label>{{ $t(`${namespace}.newDueDateLabel`) }}</label>
          <v-text-field
            v-model="newDueDate"
            ref="newDueDate"
            placeholder="DD/MM/AAAA"
            slot="activator"
            flat
            outline
            solo
            color="accent"
            append-icon="today"
            :name="$t(`${namespace}.newDueDateName`)"
            mask="##/##/####"
            return-masked-value
            v-validate="{
              required: true,
              date_format: 'dd/MM/yyyy',
              minDateAllowed: true
            }"
            :error-messages="errors.collect($t(`${namespace}.newDueDateName`))"
            :data-vv-name="$t(`${namespace}.newDueDateName`)"
            required
            autocomplete="off"
            :loading="status === isLoading"
            :disabled="status === isLoading"
            @click:append="newDueDateCalendar = true"
            @change="getDuplicateOrder"
          />

          <v-dialog
            v-model="newDueDateCalendar"
            ref="calendar"
            persistent
            lazy
            full-width
            width="290"
            class="v-dialog__container--first"
          >
            <v-date-picker
              v-model="newDueDateUnformatted"
              :locale="currentLocale"
              :min="minPeriod.date"
              color="accent"
              @change="setNewDueDate"
            />
          </v-dialog>
        </div>
      </fieldset>
    </form>

    <Preview
      :data="{ ...form, ...newOrderDetails }"
      :status="status"
      @toggleResetValue="toggleResetValue"
    />

    <ReasonDialog
      :visible="reasonDialog"
      @close="closeReasonDialog"
      @submit="createDuplicateOrder"
    />
  </section>
</template>

<script>
import moment from 'moment'
import { VMoney } from 'v-money'
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, goBack } from '@/utils/functions'
import Preview from './Preview'
import ReasonDialog from './ReasonDialog'

export default {
  name: 'Form',
  props: {
    kind: {
      type: String
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  directives: {
    money: VMoney
  },
  components: {
    Preview,
    ReasonDialog
  },
  computed: {
    ...mapState({
      globalCompany: (state) => state.company.selectedCompany,
      isAdmin: ({ session }) => session.user.business.isAdministrator
    }),

    invoiceId () {
      return this.$route.params.invoiceId
    },

    today () {
      return new Date().toJSON().slice(0, 10)
    },

    minPeriod () {
      const momentJsDate = moment()
      const todayDate = momentJsDate.format('YYYY-MM-DD')
      const todayMilliseconds = Number(momentJsDate.format('x'))

      const dueDateOpt = new Date(Number(this.$route.params.dueDateOpt))
      const dueDateOptAddOneday = moment(dueDateOpt).add(1, 'day').format('YYYY-MM-DD')
      const dueDateOptAddOnedayMilliseconds = Number(moment(dueDateOptAddOneday).format('x'))

      const date = dueDateOptAddOnedayMilliseconds > todayMilliseconds
        ? dueDateOptAddOneday
        : todayDate
      const milliseconds = dueDateOptAddOnedayMilliseconds > todayMilliseconds
        ? dueDateOptAddOnedayMilliseconds
        : todayMilliseconds

      return {
        date,
        milliseconds
      }
    },

    maxPeriod () {
      const momentJsDate = this.form.newDueDate
        ? moment(`${this.convertDateToEnFormat(this.form.newDueDate)}T00:00:00`).add(6, 'days').add(this.maxPeriodNumber, 'months')
        : moment().add(6, 'days').add(this.maxPeriodNumber, 'months')

      return {
        date: momentJsDate.format('YYYY-MM-DD'),
        milliseconds: Number(momentJsDate.format('x'))
      }
    },

    currentLocale () {
      return this.$i18n.locale.split('_').join('-')
    },

    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.InvoiceExtendDueDate.form',
      isFetching: false,
      reasonDialog: false,
      maxPeriodNumber: 3,

      // mask money config
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },

      // details
      newOrderDetails: {},

      // calendar
      newDueDateCalendar: false,

      // form fields
      newDueDateUnformatted: null,
      newDueDate: null,
      form: {
        newDueDate: null,
        applyServiceCharge: true,
        applyPenalty: true,
        applyInterest: true,
        reason: null
      }
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    prevPage () {
      goBack()
    },

    openReasonDialog () {
      this.reasonDialog = true
    },

    closeReasonDialog () {
      this.reasonDialog = false
    },

    convertDateToEnFormat (str) {
      return str.split('/').reverse().join('-')
    },

    setNewDueDate (val) {
      this.newDueDate = moment(val).format('DD/MM/YYYY')
      this.newDueDateUnformatted = moment(val).format('YYYY-MM-DD')
      this.form.newDueDate = moment(val).format('YYYY-MM-DD')
      this.newDueDateCalendar = false

      const input = this.$refs['newDueDate'].$el.querySelector('input')

      if (input) {
        input.focus()
        setTimeout(() => { input.blur() }, 100)
      }
    },

    resetFields () {
      this.newDueDateUnformatted = null
      this.newDueDate = null
      this.newOrderDetails = {}
      this.form = {
        newDueDate: null,
        applyServiceCharge: true,
        applyPenalty: true,
        applyInterest: true,
        reason: null
      }
    },

    toggleResetValue (field) {
      if (field === 'serviceCharge') {
        this.form.applyServiceCharge = !this.form.applyServiceCharge
        return
      }

      if (field === 'penalty') {
        this.form.applyPenalty = !this.form.applyPenalty
        return
      }

      if (field === 'interest') {
        this.form.applyInterest = !this.form.applyInterest
      }
    },

    async getDuplicateOrder () {
      if (this.status === this.isLoading) return

      try {
        const isValid = await this.$validator.validateAll()
        if (isValid) {
          this.$emit('setCurrentStatus', this.isLoading)

          const { newDueDate } = this
          this.form.newDueDate = this.convertDateToEnFormat(newDueDate)

          const { data: res } = await this.$apiService.getDuplicateOrder(this.invoiceId, {
            newDueDate: this.convertDateToEnFormat(newDueDate)
          })

          if (!res.valid || !res.content) {
            const message = this.isValidArray(res.reasons)
              ? res.reasons[0]
              : this.$t(`${this.namespace}.duplicateOrderError`)

            this.showAlert({ type: 'error', message })
            this.$emit('setCurrentStatus', this.isFailed)
            return
          }

          const {
            chargeBacksSubtotal = 0,
            creditsSubtotal = 0,
            discountsSubtotal = 0,
            duplicateInvoiceSubtotal = 0,
            interestSubtotal = 0,
            orderSubtotal = 0,
            penaltySubtotal = 0,
            ridesSubtotal = 0,
            saasSubtotal = 0
          } = res.content

          this.newOrderDetails = {
            chargeBacksSubtotal,
            creditsSubtotal,
            discountsSubtotal,
            duplicateInvoiceSubtotal,
            interestSubtotal,
            orderSubtotal,
            penaltySubtotal,
            ridesSubtotal,
            saasSubtotal
          }
        }

        this.$emit('setCurrentStatus', this.isSuccess)
      } catch (err) {
        console.log('ERROR:', err)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.duplicateOrderError`)
        })

        this.$emit('setCurrentStatus', this.isFailed)
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        if (isValid) this.openReasonDialog()
      } catch (err) {
        console.log('ERROR', err)
      }
    },

    updateReason (reason) {
      this.form.reason = reason
    },

    isValidArray (val) {
      return isArray(val)
    },

    async createDuplicateOrder (reason) {
      try {
        if (reason) {
          this.$emit('setCurrentStatus', this.isLoading)
          this.updateReason(reason)

          const { data: res } = await this.$apiService.createDuplicateOrder(this.invoiceId, this.form)

          if (!res.valid) {
            const message = this.isValidArray(res.reasons)
              ? res.reasons[0]
              : this.$t(`${this.namespace}.submitError`)
            this.$emit('setCurrentStatus', this.isFailed)

            // TO DO
            if (/chegou\s+ao\s+limite/i.test(message)) {
              return this.$router.push({ name: 'InvoiceExtendDueDateSoldOutRequests' })
            }

            this.showAlert({ type: 'error', message })

            return
          }

          this.$emit('setCurrentStatus', this.isSuccess)

          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.submitSuccess`)
          })

          setTimeout(() => {
            this.resetFields()
            this.$router.push({ name: 'Financial' })
          }, 2000)
        }
      } catch (err) {
        console.log('ERROR:', err)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.submitError`)
        })
      }
    }
  },
  watch: {
    submit () {
      this.handleSubmit()
    },

    newDueDate (val) {
      if (!val) this.resetFields()
    }
  },
  created () {
    moment.locale(this.currentLocale)

    this.$validator.extend('minDateAllowed', {
      getMessage: field => this.$t(`${this.namespace}.minDateAllowed`),
      validate: value => {
        const minPeriod = this.minPeriod.milliseconds
        const date = Number(moment(`${this.convertDateToEnFormat(value)}T23:59:59`).format('x'))

        if (!value || value.length < 10 || date < minPeriod) return false
        return moment(this.convertDateToEnFormat(value))._isValid
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.form-fw__content--small {
  max-width: 405px;
}

.reset {
  color: $accent;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
